<script setup lang="ts">
import { SancareBreadcrumb, SancareOcticon, SancareToggle } from '@sancare/typescript-commons'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { useBreadcrumb } from '@/breadcrumbs/useBreadcrumb'
import router from '@/router'
import {
  actionLabels,
  AutomationActionEnum,
  AutomationGroup,
  AutomationTypeEnum,
  GhmListType,
  GroupSearchOpEnum
} from '@/store/modules/automation/types'
import { ModeEnum } from '@/store/modules/settings/types'

import AutomationActionEditor from './AutomationActionEditor.vue'
import AutomationCriteriaGroups from './AutomationCriteriaGroups.vue'

const typeList = ['M', 'C', 'K', 'Z']
const levelList = ['1', '2', '3', '4', 'A', 'B', 'C', 'D', 'J', 'T', 'Z']
const cmdList = Array(29).fill('').map((_, i) => (i + 1).toString().padStart(2, '0'))
const subCmdList = Array(63).fill('').map((_, i) => (i + 1).toString().padStart(2, '0'))

const store = useStore()
const { t } = useI18n()
const { breadcrumbItems } = useBreadcrumb()

const PREVIOUS_STAY_MAX_DAYS_OPTIONS = [30, 90, 180, 270, 360]
const DEFAULT_PREVIOUS_STAY_MAX_DAYS = 90

const requestError = ref<string | null>(null)
const requestSuccess = ref<string | null>(null)
const additionMode = ref(true)
const ghmCmd = ref('*')
const ghmType = ref('*')
const ghmSubCmd = ref('*')
const ghmLevel = ref('*')
const enableWebpimsAccess = computed(() => store.state.settings.connectorSettings.connectorTypes.includes('webpims'))
const automationGroup = computed<AutomationGroup>(() => store.state.automation.editedAutomationGroup)
const addAutomationGroupRequest = computed(() => store.state.automation.addAutomationGroupRequest)
const updateAutomationGroupRequest = computed(() => store.state.automation.updateAutomationGroupRequest)
const getAutomationGroupRequest = computed(() => store.state.automation.getAutomationGroupRequest)

const editedGroupId = computed<string | null>(() => store.state.route.query?.groupId || null)
const ghmCollidingGroupNames = computed<string[]>(() => store.state.automation.ghmCollidingGroupNames)
const ghmUnknown = computed<string[]>(() => store.state.automation.ghmUnknown)
const automationType = computed<AutomationTypeEnum>(() => {
  const baseType = editedGroupId.value
    ? automationGroup.value.type
    : (store.state.route.query.automationType as AutomationTypeEnum || AutomationTypeEnum.AI)
  return baseType === AutomationTypeEnum.AI ? AutomationTypeEnum.AI : AutomationTypeEnum.RULES
})
const hasPreviousStayCondition = computed<boolean>(() =>
  automationGroup.value.globalCriteriaGroup?.criteriaList?.some(
    (c) => c.type.startsWith('previousStay')
  ) ?? false
)
const conditionsExplanationSentence = computed(() => automationType.value !== AutomationTypeEnum.AI
  ? t('automation.editor.conditionsExplanationRules')
  : t('automation.editor.conditionsExplanationAi')
)

const COPY_ACTIONS = [AutomationActionEnum.COPY_DP, AutomationActionEnum.COPY_DR, AutomationActionEnum.COPY_DAS]

watch(hasPreviousStayCondition, () => {
  if (hasPreviousStayCondition.value && !automationGroup.value.previousStayMaxDays) {
    automationGroup.value.previousStayMaxDays = DEFAULT_PREVIOUS_STAY_MAX_DAYS
  }
  if (!hasPreviousStayCondition.value) {
    automationGroup.value.actions
      .filter((action) => COPY_ACTIONS.includes(action.action))
      .forEach((action) => store.commit('removeAutomationGroupAction', action))
  }
})
watch(addAutomationGroupRequest, (req) => {
  if (req.error) {
    requestSuccess.value = null
    requestError.value = req.error
  } else if (req.ok) {
    requestError.value = null
    requestSuccess.value = t('automation.editor.saved')
  }
})

watch(updateAutomationGroupRequest, (req) => {
  if (req.error) {
    requestSuccess.value = null
    requestError.value = req.error
  } else if (req.ok) {
    requestError.value = null
    requestSuccess.value = t('automation.editor.updated')
  }
})

watch(() => automationGroup.value?.name, (name) => {
  if (editedGroupId.value && name && store.state.route.query?.staylistLabel !== name) {
    router.replace({ query: { ...store.state.route.query, staylistLabel: name } })
  }
}, { immediate: true })

onMounted(() => {
  if (editedGroupId.value) {
    store.dispatch('getAutomationGroup', editedGroupId.value)
      .then(() => {
        store.dispatch('getGhmWarnings', {
          presentGhmList: automationGroup.value.presentGhmList,
          absentGhmList: automationGroup.value.absentGhmList,
          groupId: automationGroup.value.id || null
        })
      })
  } else {
    store.commit('getAutomationGroupReset', automationType.value)
    store.dispatch('getGhmWarnings', {
      presentGhmList: automationGroup.value.presentGhmList,
      absentGhmList: automationGroup.value.absentGhmList,
      groupId: null
    })
  }
})

function clearMessage() {
  requestSuccess.value = null
  requestError.value = null
}

function setGroupName(name: string) {
  clearMessage()
  store.commit('setGroupName', name)
}

function setWaitDuration(waitDuration: number) {
  clearMessage()
  store.commit('setWaitDuration', Number(waitDuration))
}

function setPreviousStayMaxDays(days: number) {
  clearMessage()
  store.commit('setPreviousStayMaxDays', Number(days))
}

function setFulfillAnapathRequests(anapath: boolean) {
  clearMessage()
  store.commit('setFulfillAnapathRequests', anapath)
}

function setWaitForAnesthesia(anesthesia: boolean) {
  clearMessage()
  store.commit('setWaitForAnesthesia', anesthesia)
}

function setAlertNoCro(alertNoCro: boolean) {
  clearMessage()
  store.commit('setAlertNoCro', alertNoCro)
}

function setAlertNoCrh(alertNoCrh: boolean) {
  clearMessage()
  store.commit('setAlertNoCrh', alertNoCrh)
}

function populateGhmList() {
  clearMessage()
  if (! ghmCmd.value || ! ghmType.value || !ghmSubCmd.value || ! ghmLevel.value) {
    return
  }

  const ghm = `${ghmCmd.value}${ghmType.value}${ghmSubCmd.value}${ghmLevel.value}`.replaceAll(/\*+/g, '*')

  store.commit('populateGhmList', { additionMode: additionMode.value, ghm })
  store.dispatch('getGhmWarnings', {
    presentGhmList: automationGroup.value.presentGhmList,
    absentGhmList: automationGroup.value.absentGhmList,
    groupId: automationGroup.value.id || null
  })
}

function removeGhmFromList(type: GhmListType, value: string) {
  clearMessage()
  store.commit('removeGhmFromList', { type, value })
  store.dispatch('getGhmWarnings', {
    presentGhmList: automationGroup.value.presentGhmList,
    absentGhmList: automationGroup.value.absentGhmList,
    groupId: automationGroup.value.id || null
  })
}

function switchOperators() {
  clearMessage()
  store.commit(
    'updateOperators',
    automationGroup.value.searchOperators === GroupSearchOpEnum.ORAND
      ? GroupSearchOpEnum.ANDOR : GroupSearchOpEnum.ORAND
  )
}

function hasOneOrMoreCondition(automationGroup: AutomationGroup) {
  for (const criteriaGroup of automationGroup.criteriaGroups) {
    if (criteriaGroup.criteriaList.length) {
      return true
    }
  }
  return false
}

function saveAutomationGroup() {
  const automGp = { ...automationGroup.value, type: automationType.value }
  if (! automGp.name || automGp.name === '') {
    requestError.value = t('automation.editor.errors.nameRequired')
    return
  }

  if (automationType.value === AutomationTypeEnum.AI && !automGp.presentGhmList.length) {
    requestError.value = t('automation.editor.errors.ghmRequired')
    return
  }

  if (automationType.value === AutomationTypeEnum.RULES) {
    if (automGp.actions.every((action) => ![AutomationActionEnum.ADD_DP, AutomationActionEnum.COPY_DP].includes(action.action))) {
      requestError.value = t('automation.editor.errors.missingDpAction')
      return
    }
    if (!hasOneOrMoreCondition(automGp)) {
      requestError.value = t('automation.editor.errors.noCondition')
      return
    }
  }

  if (!automGp.waitDuration && automGp.waitDuration !== 0) {
    requestError.value = t('automation.editor.errors.waitDurationRequired')
    return
  }

  if (automGp.waitDuration < 0 || automGp.waitDuration > 30) {
    requestError.value = t('automation.editor.errors.waitDurationInvalid')
    return
  }

  if (!editedGroupId.value) {
    store.dispatch('addAutomationGroup', automGp).then(() => {
      // When the request succeeds, this.automationGroup has an id
      if (automationGroup.value.id) {
        router.replace({
          path: store.state.route.path,
          query: {
            groupId: automationGroup.value.id,
            mode: ModeEnum.AUTOMATION,
            automationType: automationType.value as string,
            staylistLabel: automationGroup.value.name
          }
        })
      }
    })
    return
  }
  store.dispatch('updateAutomationGroup', { id: editedGroupId.value, automationGroup: automGp })
}

function saveAction(action) {
  store.commit('addAutomationGroupAction', action)
}

function removeAction(action) {
  store.commit('removeAutomationGroupAction', action)
}
</script>


<template>
  <div class="container">
    <sancare-breadcrumb :items="breadcrumbItems" />
    <div class="stay-list-criteria card autom-group-editor shadow">
      <div class="card-header">
        <h1>
          {{ (automationGroup.id !== null || getAutomationGroupRequest.fetching)
            ? t('automation.editor.headerEditing')
            : t('automation.editor.headerCreating') }}
          {{ automationType === AutomationTypeEnum.AI
            ? t('automation.editor.subtitleAi')
            : t('automation.editor.subtitleRule') }}
        </h1>
      </div>
      <div class="card-body">
        <h2 class="mb-4">
          {{ t('automation.editor.generalInfo') }}
        </h2>
        <div class="row align-items-center mb-3">
          <div class="col-3 fw-bold text-end">
            {{ t('automation.editor.groupName') }}
          </div>
          <div class="col-3">
            <input
              :value="automationGroup.name"
              :disabled="getAutomationGroupRequest.fetching"
              type="text"
              class="form-control"
              @input="event => setGroupName((event.target as HTMLInputElement).value)"
            >
          </div>
        </div>
      </div>
      <div
        v-if="automationType === AutomationTypeEnum.AI"
        class="card-body"
      >
        <h2 class="mb-4">
          {{ t('automation.editor.ghmFilter') }}
        </h2>
        <div>
          <div class="row align-items-center mb-3">
            <div class="col-3 fw-bold text-end">
              {{ t('automation.editor.ghmCriteria') }}
            </div>
            <div class="col-9 row">
              <div class="col-5">
                <div class="ghm-selector">
                  <div class="ghm-selector__cmd">
                    <label>CMD</label>
                    <select
                      v-model="ghmCmd"
                      :disabled="getAutomationGroupRequest.fetching"
                      name="ghm-cmd"
                      @keyup.enter="populateGhmList"
                    >
                      <option value="*">
                        *
                      </option>
                      <option
                        v-for="i in cmdList"
                        :key="i"
                        :value="i"
                      >
                        {{ i }}
                      </option>
                    </select>
                  </div>
                  <div class="ghm-selector__type">
                    <label>Type</label>
                    <select
                      v-model="ghmType"
                      :disabled="getAutomationGroupRequest.fetching"
                      name="ghm-type"
                      @keyup.enter="populateGhmList"
                    >
                      <option value="*">
                        *
                      </option>
                      <option
                        v-for="i in typeList"
                        :key="i"
                        :value="i"
                      >
                        {{ i }}
                      </option>
                    </select>
                  </div>
                  <div class="ghm-selector__sub-cmd">
                    <label>{{ t('automation.editor.ghmNumber') }}</label>
                    <select
                      v-model="ghmSubCmd"
                      :disabled="getAutomationGroupRequest.fetching"
                      name="ghm-sub-cmd"
                      @keyup.enter="populateGhmList"
                    >
                      <option value="*">
                        *
                      </option>
                      <option
                        v-for="i in subCmdList"
                        :key="i"
                        :value="i"
                      >
                        {{ i }}
                      </option>
                    </select>
                  </div>
                  <div class="ghm-selector__level">
                    <label>{{ t('automation.editor.ghmLevel') }}</label>
                    <select
                      v-model="ghmLevel"
                      :disabled="getAutomationGroupRequest.fetching"
                      name="ghm-level"
                      @keyup.enter="populateGhmList"
                    >
                      <option value="*">
                        *
                      </option>
                      <option
                        v-for="i in levelList"
                        :key="i"
                        :value="i"
                      >
                        {{ i }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="col-4">
                <select
                  v-model="additionMode"
                  :disabled="getAutomationGroupRequest.fetching"
                  class="form-select"
                >
                  <option :value="true">
                    {{ t('automation.editor.ghmAdd') }}
                  </option>
                  <option :value="false">
                    {{ t('automation.editor.ghmExclude') }}
                  </option>
                </select>
              </div>
              <div class="col-auto">
                <div class="input-group">
                  <button
                    class="btn"
                    :class="{
                      'btn-primary': additionMode,
                      'btn-danger': !additionMode,
                    }"
                    :disabled="getAutomationGroupRequest.fetching"
                    type="button"
                    @click="populateGhmList"
                  >
                    {{ additionMode ? t('automation.editor.ghmAddBtn') : t('automation.editor.ghmExcludeBtn') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="row align-items-center">
            <div class="offset-3 col-auto">
              <v-dropdown
                v-if="ghmCollidingGroupNames.length"
                :triggers="['hover']"
                placement="top"
                popover-class="criteria-popover"
              >
                <span>
                  <sancare-octicon
                    name="alert"
                    :width="30"
                    :height="30"
                  />
                </span>
                <slot />
                <template #popper>
                  <div
                    class="m-2"
                    style="width: 30rem;"
                  >
                    {{ t('automation.editor.ghmCollidingWarning') }}
                    <div style="text-align: center; font-weight: bold;">
                      "{{ ghmCollidingGroupNames.join('", "') }}"
                    </div>
                    <hr>
                    {{ t('automation.editor.ghmCollidingReminder') }}<br>
                    <small>
                      {{ t('automation.editor.ghmCollidingReminderText') }}
                    </small>
                  </div>
                </template>
              </v-dropdown>
              <v-dropdown
                v-if="ghmUnknown.length"
                :triggers="['hover']"
                placement="top"
                class="col"
                popover-class="criteria-popover"
              >
                <span>
                  <sancare-octicon
                    name="alert"
                    :width="30"
                    :height="30"
                  />
                </span>
                <slot />
                <template #popper>
                  <div style="width: 30rem;">
                    {{ t('automation.editor.ghmUnknownWarning') }}
                    <div style="text-align: center; font-weight: bold;">
                      "{{ ghmUnknown.join('", "') }}"
                    </div>
                  </div>
                </template>
              </v-dropdown>
            </div>
          </div>
          <div
            v-if="automationGroup.presentGhmList && automationGroup.presentGhmList.length > 0"
            class="row align-items-center"
          >
            <div class="offset-3 col-auto">
              {{ t('automation.editor.ghmPresent') }}
            </div>
            <div class="col row g-0 py-2">
              <div>
                <div
                  v-for="(value, idx) in automationGroup.presentGhmList"
                  :key="`${idx}`"
                  class="col-auto mx-1 mb-1 ps-2 pe-2 badge text-bg-primary"
                >
                  <span>{{ value }}</span>
                  <span
                    @click="removeGhmFromList('presentGhmList', value)"
                  >
                    <sancare-octicon
                      name="x"
                      :width="14"
                      :height="11"
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="automationGroup.absentGhmList && automationGroup.absentGhmList.length > 0"
            class="row align-items-center"
          >
            <div class="offset-3 col-auto">
              {{ t('automation.editor.ghmAbsent') }}
            </div>
            <div class="col row g-0 py-2">
              <div>
                <div
                  v-for="(value, idx) in automationGroup.absentGhmList"
                  :key="`${idx}`"
                  class="col-auto mx-1 mb-1 ps-2 pe-2 badge text-bg-primary"
                >
                  <span>{{ value }}</span>
                  <span
                    @click="removeGhmFromList('absentGhmList', value)"
                  >
                    <sancare-octicon
                      name="x"
                      :width="14"
                      :height="11"
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body">
        <h2 class="mb-2">
          {{ t('automation.editor.conditions') }}
        </h2>
        <div class="mb-4 fst-italic text-secondary">
          {{ conditionsExplanationSentence }}
        </div>
        <automation-criteria-groups
          :disabled="getAutomationGroupRequest.fetching"
          :automation-type="automationType"
          @clear-message="clearMessage"
        />
        <sancare-toggle
          class="py-2 align-items-center justify-content-center"
          :label="t('automation.editor.invertOperators')"
          :label-position="'left'"
          :value="automationGroup.searchOperators === 'orAnd'"
          :disabled="getAutomationGroupRequest.fetching"
          @toggle-change="switchOperators"
        />
        <sancare-toggle
          v-if="automationType === AutomationTypeEnum.AI"
          class="py-2 align-items-center justify-content-center"
          :label="t('automation.editor.waitBiopsy')"
          :label-position="'left'"
          :value="automationGroup.fulfillAnapathRequests"
          :disabled="getAutomationGroupRequest.fetching"
          @toggle-change="setFulfillAnapathRequests(!automationGroup.fulfillAnapathRequests)"
        />
        <sancare-toggle
          v-if="automationType === AutomationTypeEnum.AI"
          class="py-2 align-items-center justify-content-center"
          :label="t('automation.editor.waitAnesthesia')"
          :label-position="'left'"
          :value="automationGroup.waitForAnesthesia"
          :disabled="getAutomationGroupRequest.fetching"
          @toggle-change="setWaitForAnesthesia(!automationGroup.waitForAnesthesia)"
        />
        <sancare-toggle
          v-if="enableWebpimsAccess"
          class="py-2 align-items-center justify-content-center"
          :label="t('automation.editor.alertNoCro')"
          :label-position="'left'"
          :value="automationGroup.alertNoCro"
          :disabled="getAutomationGroupRequest.fetching"
          @toggle-change="setAlertNoCro(!automationGroup.alertNoCro)"
        />
        <sancare-toggle
          v-if="enableWebpimsAccess"
          class="py-2 align-items-center justify-content-center"
          :label="t('automation.editor.alertNoCrh')"
          :label-position="'left'"
          :value="automationGroup.alertNoCrh"
          :disabled="getAutomationGroupRequest.fetching"
          @toggle-change="setAlertNoCrh(!automationGroup.alertNoCrh)"
        />
        <div
          v-if="hasPreviousStayCondition"
          class="row py-2 g-0 align-items-center justify-content-center"
        >
          <div class="col-auto">
            {{ t('automation.editor.previousStayPrefix') }}
          </div>
          <div class="col-auto px-2">
            <select
              :value="automationGroup.previousStayMaxDays"
              :disabled="getAutomationGroupRequest.fetching"
              class="form-select"
              @change="event => setPreviousStayMaxDays((event.target as HTMLSelectElement).value)"
            >
              <option
                v-for="days in PREVIOUS_STAY_MAX_DAYS_OPTIONS"
                :key="days"
                :value="days"
              >
                {{ days }}
              </option>
            </select>
          </div>
          <div class="col-auto">
            {{ t('automation.editor.previousStaySuffix') }}
          </div>
        </div>
        <div class="row py-2 g-0 align-items-center justify-content-center">
          <div class="col-auto ">
            {{ t('automation.editor.waitDurationPrefix') }}
          </div>
          <div class="col-1 px-2">
            <input
              :value="automationGroup.waitDuration"
              :disabled="getAutomationGroupRequest.fetching"
              type="number"
              max="30"
              min="0"
              class="form-control"
              @change="event => setWaitDuration((event.target as HTMLInputElement).value)"
            >
          </div>
          <div class="col-auto">
            {{ t('automation.editor.waitDurationSuffix') }}
          </div>
        </div>
        <div class="row py-2 g-0 align-items-center justify-content-center fst-italic text-secondary">
          {{ t('automation.editor.waitDurationNote') }}
        </div>
      </div>
      <div
        v-if="automationType === AutomationTypeEnum.RULES"
        id="rules-group-actions"
        class="card-body"
      >
        <h2 class="mb-4">
          {{ t('automation.editor.actionsTitle') }}
        </h2>
        <div>
          <div class="align-items-center mb-3">
            <automation-action-editor
              :show-copy-actions="hasPreviousStayCondition"
              @save-action="saveAction"
            />
          </div>
          <div class="row">
            <div class="col-6 offset-3">
              <div
                v-for="(action, idx) in automationGroup.actions"
                :key="`${idx}`"
                class="col-auto mx-1 mb-1 ps-2 pe-2 badge text-bg-primary"
              >
                <span>{{ actionLabels[action.action] }}<template v-if="action.value"> : {{ action.value }}</template></span>
                <span
                  class="cp"
                  @click="removeAction(action)"
                >
                  <sancare-octicon
                    name="x"
                    :width="14"
                    :height="11"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <div class="row g-0 align-items-center">
          <div class="col">
            <div
              v-if="requestError"
              class="text-danger"
            >
              {{ requestError }}
            </div>
            <div
              v-else-if="requestSuccess"
              class="text-success"
            >
              {{ requestSuccess }}
            </div>
          </div>
          <div class="col-auto ms-auto">
            <button
              id="butSave"
              v-tooltip="automationGroup.automated ? t('automation.editor.saveTooltip') : null"
              class="btn btn-primary"
              :disabled="addAutomationGroupRequest.fetching || updateAutomationGroupRequest.fetching"
              @click="saveAutomationGroup"
            >
              {{ automationGroup.automated ? t('automation.editor.saveAndDeactivate') : t('automation.editor.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:color';
@import '@sancare/typescript-commons/src/assets/styles/variables';

h1 {
  font-size: 1.5rem;
}

h2 {
  font-size: 1.1rem;
}

.autom-group-editor {
  padding: 0;
  margin: 20px 0;

  .card-body {
    padding: 2rem;

    & ~ .card-body {
      border-top: 1px solid color.adjust($sancare-light-grey, $lightness: -30%);
    }
  }

  .card-header,
  .card-footer {
    border-color: color.adjust($sancare-light-grey, $lightness: -30%);
    padding: 1.25rem 2rem;
    background: #fbf8f8;
  }

  :deep(.toggle-switch) {
    padding: 0;
    margin: 0;
  }
}
</style>
